import type { ScheduleResult } from "@planerius/contracts/cabinet"
import type { CabinetBranchAccess } from "@planerius/contracts/cabinet-auth"
import { useEffect, useMemo, useState } from "react"

import { useCabinetAccess } from "../../access/use-access"
import { fetchJournal } from "../../api/journal.api"
import type { ApiProblem } from "../../api/problem"
import type { JournalAvailability } from "../../journal/journal-mode"
import { journalAvailability, platformFromProblem } from "../../journal/journal-mode"
import type { JournalColumn, JournalDayModel } from "../../journal/journal-model"
import { buildJournalDay } from "../../journal/journal-model"
import { minutesFromDayStart } from "../../journal/journal-time"
import { useNowMinute } from "../../journal/use-journal-clock"
import { todayInZone } from "../../lib/format"
import { reportDataAt } from "../../shell/data-freshness"
import { resolveBranch } from "../../shell/use-branch"
import { useOnline } from "../../shell/use-online"
import { useJournalDay } from "../journal/use-journal-day"
import type { MasterDay, NextShift } from "./master-day"
import { buildMasterDay } from "./master-day"
import type { MasterVisibility } from "./master-visibility"
import { HIDDEN_VISIBILITY, resolveMasterVisibility } from "./master-visibility"

/**
 * Один день мастера для всех четырёх его экранов.
 *
 * ПОЧЕМУ ОДИН ХУК НА ЧЕТЫРЕ ЭКРАНА. «Мой день», карточка записи и форма записи смотрят
 * в один и тот же день одного и того же мастера. Три отдельные загрузки означали бы три
 * разных ответа на вопрос «занято ли 14:15» на трёх соседних экранах — и запись,
 * созданную поверх собственного визита.
 *
 * ПОЧЕМУ ЗАПРОС СУЖЕН ДО СВОЕГО employeeId. Не ради приличия, а ради секунд: «Мой день»
 * открывают 5–15 раз за смену между клиентами, и день шести мастеров вместо одного —
 * это шестикратный ответ там, где пять шестых его будут выброшены. Сервер сужение
 * разрешает: employeeIds может только СУЗИТЬ выдачу, расширить её фильтром нельзя.
 *
 * ВИДИМОСТЬ ДЕНЕГ И ТЕЛЕФОНА СЧИТАЕТСЯ ЗДЕСЬ ЖЕ и отдаётся экранам готовой: настройка
 * салона обязана действовать на все экраны РАЗОМ (4-Б12), а это достигается ровно одним
 * способом — одним местом, где она вычисляется.
 */

export interface MasterSearch {
  readonly date?: string | undefined
  readonly branch?: string | undefined
}

export interface MasterDayData {
  readonly branch: CabinetBranchAccess | null
  readonly date: string
  readonly timeZone: string
  /** Карточка сотрудника вошедшего. null — человек не исполнитель, дня у него нет. */
  readonly employeeId: string | null
  readonly result: ScheduleResult | null
  readonly model: JournalDayModel | null
  readonly column: JournalColumn | null
  readonly day: MasterDay | null
  readonly visibility: MasterVisibility
  readonly availability: JournalAvailability
  readonly nowMinute: number
  readonly loading: boolean
  readonly problem: ApiProblem | null
  readonly reload: () => void
}

/**
 * Следующая смена после этого дня.
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ ЗАПРОС И ПОЧЕМУ ОН НЕ ВРЕДИТ ГЛАВНОМУ ЭКРАНУ. Состояние «сегодня
 * выходной» обязано назвать следующую смену, иначе оно бесполезно: мастер открывает
 * экран как раз затем, чтобы узнать, когда ему выходить. Но тащить неделю расписания
 * в КАЖДОЕ открытие дня ради строки, которая нужна раз в неделю, — это семикратный ответ
 * на самом горячем экране продукта. Поэтому неделя запрашивается ТОЛЬКО когда сегодня
 * смены нет, то есть в тот единственный день, когда она нужна.
 *
 * ОТСУТСТВИЕ ОТВЕТА ЗНАЧИМО: пока неделя не доехала, день считается «график не заведён»,
 * а не «выходной». Ошибиться в эту сторону безопаснее — «графика нет» отправляет
 * к администратору, а ложное «выходной» отправляет домой.
 */
function useNextShift(input: {
  readonly branchId: string | null
  readonly employeeId: string | null
  readonly date: string
  readonly timeZone: string
  readonly enabled: boolean
}): NextShift | null {
  const [shift, setShift] = useState<NextShift | null>(null)
  const { branchId, employeeId, date, timeZone, enabled } = input

  useEffect(() => {
    setShift(null)
    if (!enabled || branchId === null || employeeId === null) return undefined

    const controller = new AbortController()
    const from = new Date(`${date}T12:00:00Z`)
    from.setUTCDate(from.getUTCDate() + 1)
    const to = new Date(from)
    to.setUTCDate(to.getUTCDate() + 6)

    const run = async (): Promise<void> => {
      try {
        const result = await fetchJournal(
          {
            branchId,
            dateFrom: from.toISOString().slice(0, 10),
            dateTo: to.toISOString().slice(0, 10),
            employeeIds: [employeeId],
          },
          controller.signal,
        )
        if (controller.signal.aborted) return
        const workdays = result.workdays
          .filter((item) => item.employeeId === employeeId)
          .toSorted((a, b) => a.date.localeCompare(b.date))
        const first = workdays[0]
        if (first === undefined) return
        setShift({
          date: first.date,
          startMinute: minutesFromDayStart(first.startAt, timeZone, first.date),
          endMinute: minutesFromDayStart(first.endAt, timeZone, first.date),
        })
      } catch {
        // Молча: следующая смена — это дополнение к состоянию «выходной», а не само
        // состояние. Второй экран отказа поверх выходного дня не поможет никому.
      }
    }
    void run()

    return () => {
      controller.abort()
    }
  }, [branchId, employeeId, date, timeZone, enabled])

  return shift
}

export function useMasterDay(search: MasterSearch): MasterDayData {
  const { context, permissions } = useCabinetAccess()
  const online = useOnline()

  const branch = resolveBranch(context, search.branch ?? null)
  const timeZone = branch?.timezone ?? context.tenant.defaultTimezone
  // День календарный и по зоне ФИЛИАЛА: «сегодня» во Владивостоке наступает раньше
  // московского, и день, взятый у устройства, уехал бы на сутки.
  const date = search.date ?? todayInZone(timeZone)
  const employeeId = context.employeeId
  const branchId = branch?.id ?? null

  const query = useMemo(
    () =>
      branchId === null || employeeId === null
        ? null
        : {
            branchId,
            dateFrom: date,
            dateTo: date,
            employeeIds: [employeeId],
          },
    [branchId, date, employeeId],
  )

  const loaded = useJournalDay(query)
  const result = loaded.result
  const nowMinute = useNowMinute(timeZone, date)

  const model = useMemo(
    () => (result === null ? null : buildJournalDay(result, date, { includeCancelled: false })),
    [result, date],
  )

  const column = useMemo(
    () =>
      model === null ? null : (model.columns.find((item) => item.employee.id === employeeId) ?? null),
    [model, employeeId],
  )

  const hasShiftToday = column !== null && column.workStartMinute !== null
  const nextShift = useNextShift({
    branchId,
    employeeId,
    date,
    timeZone,
    enabled: model !== null && !hasShiftToday,
  })

  const day = useMemo(
    () => (model === null ? null : buildMasterDay({ model, employeeId, nowMinute, nextShift })),
    [model, employeeId, nowMinute, nextShift],
  )

  // Видимость считается по СВОИМ записям: настройка салона открывает мастеру его строки,
  // и судить по чужой строке о своей видимости нельзя.
  const visibility = useMemo(() => {
    if (result === null || employeeId === null) return HIDDEN_VISIBILITY
    return resolveMasterVisibility({
      permissions,
      appointments: result.appointments.filter((item) => item.employeeId === employeeId),
    })
  }, [result, employeeId, permissions])

  const problemCode = loaded.problem?.code ?? null
  const availability = journalAvailability({
    online,
    problemCode,
    hasData: result !== null,
    platform: platformFromProblem(problemCode),
    supportReadOnly: context.impersonation?.readOnly === true,
    dataAt: result?.generatedAt ?? null,
    timeZone,
    cached: false,
  })

  // Момент данных уходит в общий источник свежести: полоса «Нет связи» обязана назвать
  // время, даже если она появилась уже после загрузки.
  const generatedAt = result?.generatedAt ?? null
  useEffect(() => {
    if (generatedAt !== null) reportDataAt(generatedAt)
  }, [generatedAt])

  return {
    branch,
    date,
    timeZone,
    employeeId,
    result,
    model,
    column,
    day,
    visibility,
    availability,
    nowMinute,
    loading: loaded.loading,
    problem: loaded.problem,
    reload: loaded.reload,
  }
}
